import { StringHelper } from 'lib_ts/classes/string.helper';
import { BPSortMode } from 'lib_ts/enums/bp-summary.enums';
import { Competition } from 'lib_ts/enums/team.enums';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import { ISortSelection } from 'lib_ts/interfaces/common/i-option';
import { IBPSummary } from 'lib_ts/interfaces/i-bp-summary';
import { cloneDeep, intersection } from 'lodash';
import { create } from 'zustand';

interface IFilters {
  machineIDs: Nullable<string[]>;
  // null => not filtering, otherwise team must be included to show
  // accounts for case where you exclude the last/only available thing
  teamIDs: Nullable<string[]>;
  inGame: Nullable<boolean>;
  competitions: Nullable<Competition[]>;
}

type BpSummaryState = {
  // key is a v4 ID, value is the component identifier
  open: boolean;
  lastUpdated: Date | undefined;
  summaries: IBPSummary[];
  filters: IFilters;
  sortMode: ISortSelection;
};

type BpSummaryActions = {
  toggleOpen: () => void;
  setSummaries: (v: IBPSummary[]) => void;
  mergeFilters: (v: Partial<IFilters>) => void;
  resetFilters: () => void;
  setSortMode: (v: ISortSelection) => void;
};

type BpSummaryStore = BpSummaryState & BpSummaryActions;

const useBpSummaryStore = create<BpSummaryStore>((set, get) => ({
  open: false,
  toggleOpen: () => {
    const { open } = get();
    set({ open: !open });
  },

  lastUpdated: undefined,

  summaries: [],
  setSummaries: (nextSummaries) => {
    const { filters } = get();

    // sort each summary's events by newest first
    nextSummaries.forEach((s) =>
      s.history.sort(
        (a, b) => -StringHelper.safeSort(a.fireCreated, b.fireCreated)
      )
    );

    // existing filters should only contain valid selections wrt the latest summaries
    const nextFilters = cloneDeep(filters);

    const validMachineIDs = intersection(
      nextFilters.machineIDs,
      nextSummaries.map((m) => m.machineID)
    );

    nextFilters.machineIDs =
      validMachineIDs.length === 0
        ? null // * reset the filter if there is no intersection
        : validMachineIDs;

    const validTeams = intersection(
      nextFilters.teamIDs,
      nextSummaries.map((m) => m.teamName)
    );

    nextFilters.teamIDs =
      validTeams.length === 0
        ? null // * reset the filter if there is no intersection
        : validTeams;

    set({
      summaries: nextSummaries,
      lastUpdated: new Date(),
      filters: nextFilters,
    });
  },

  filters: {
    machineIDs: null,
    teamIDs: null,
    inGame: null,
    competitions: null,
  },
  mergeFilters: (v) => {
    const { filters } = get();

    set({
      filters: {
        ...filters,
        ...v,
      },
    });
  },
  resetFilters: () => {
    set({
      filters: {
        machineIDs: null,
        teamIDs: null,
        inGame: null,
        competitions: null,
      },
    });
  },

  sortMode: {
    value: BPSortMode.LastFired,
    dir: -1,
  },
  setSortMode: (v) => set({ sortMode: v }),
}));

export default useBpSummaryStore;
